import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';

import HeartIcon from 'mdi-react/HeartIcon';
import HeartOutlineIcon from 'mdi-react/HeartOutlineIcon';
import BellIcon from 'mdi-react/BellIcon';
import BellOutlineIcon from 'mdi-react/BellOutlineIcon';
import CompareIcon from 'mdi-react/ScaleBalanceIcon';
import { useAppContext } from '@prisjakt/ui-app-context';
import Dropdown from '@prisjakt/ui-dropdown-new';

import LoginPromoModal from '../../../../../../components/modal/login-promo';
import {
  openAlerts,
  removeAlertRequest,
} from '../../../../../sidebar/actions/alert';
import {
  selectList,
  addListItemRequest,
  addItemToMyProductsRequest,
} from '../../../../../sidebar/actions/lists';
import { headerClickEvent } from '../../../../actions/product';
import { showModal } from '../../../../../../components/modal/actions';
import { PRODUCT_ALERT_MODAL_ID } from '../../../../constants';
import { useIsAuthenticated } from '../../../../../../hooks/use-is-authenticated';

import { AppState } from '../../../../../../reducers';
import { ProductPageQuery } from '../../../../../../types';
import { UserProductAlert } from '../../../../../sidebar/types';

import * as S from './styles';

type Me = {
  lists: {
    nodes: [{ id: number; name: string; inventory: boolean }];
  };
};

export type ButtonStyle = 'square' | 'circle';

type Props = {
  horizontal?: boolean;
  isDesktop?: boolean;
  product: NonNullable<ProductPageQuery['product']>;
  buttonStyle?: ButtonStyle;
  className?: string;
};

type ActionType = 'list-add' | 'alert-remove' | 'alert-create';

const ProductActionButtons = ({
  className,
  horizontal = false,
  isDesktop = false,
  buttonStyle = 'square',
  product,
}: Props) => {
  const { t, linkTo } = useAppContext();
  const dispatch = useDispatch();

  const me: Me = useSelector((state: AppState) => state.areas.account.me);
  const isAuthenticated = useIsAuthenticated();
  const userProductAlerts = useSelector(
    (state: AppState) => state.areas.sidebar.alerts.products
  );
  const deletedLists = useSelector(
    (state: AppState) => state.areas.sidebar.deletedLists
  );
  const selectedList = useSelector(
    (state: AppState) => state.areas.sidebar.selectedList
  );

  // Highlights the heart if the product was recently added to a list.
  // Kind of an ugly hack; should be fixed once we can get this information
  // from GraphQL.
  const [addedToList, setAddedToListState] = useState<boolean>(false);
  const [showLoginPromo, setLoginPromoState] = useState(false);

  const toggleLoginPromo = () => setLoginPromoState(!showLoginPromo);

  const onAction = (type: ActionType) => {
    dispatch(headerClickEvent(type));
    if (type === 'alert-create') {
      dispatch(showModal(PRODUCT_ALERT_MODAL_ID));
    }
  };

  const addToList = () => {
    onAction('list-add');
    dispatch(addListItemRequest(product.id, product.name, isDesktop));

    setAddedToListState(true);
  };

  const latestLists =
    me &&
    me.lists &&
    me.lists.nodes
      .filter(list => deletedLists[list.id] !== true)
      .filter(list => !list.inventory)
      .sort(a => {
        if (a.id === selectedList) {
          return 1;
        }

        return 0;
      });

  const handleListButtonClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    addToList();
  };

  const userAlert = userProductAlerts.find(
    (alert: UserProductAlert) => alert.productId === product.id
  );
  const alertId = userAlert && userAlert.alertId;

  const removeAlert = () => {
    if (!alertId) return;

    onAction('alert-remove');
    dispatch(openAlerts());
    dispatch(removeAlertRequest(alertId, product.id));
  };

  const editAlert = () => {
    dispatch(openAlerts());
    onAction('alert-create');
  };

  const addToMyProducts = () => {
    onAction('list-add');
    dispatch(addItemToMyProductsRequest(product.id, product.name, isDesktop));
    setAddedToListState(true);
  };

  return (
    <S.ProductActionButtons
      buttonStyle={buttonStyle}
      horizontal={horizontal}
      className={className}
    >
      {isAuthenticated ? (
        <Dropdown
          variant="secondary"
          grow="right"
          showArrow={false}
          toggleElement={
            <ListsButton
              buttonStyle={buttonStyle}
              isActive={addedToList}
              onClick={
                latestLists.length > 0 ? undefined : handleListButtonClick
              }
            />
          }
        >
          <>
            <S.DropdownItem
              onClick={() => {
                addToMyProducts();
              }}
            >
              {t('lists', 'my-products.title')}
            </S.DropdownItem>
            {latestLists.map(list => (
              <S.DropdownItem
                onClick={() => {
                  dispatch(selectList(list.id));
                  addToList();
                }}
                key={list.id}
              >
                {list.name}
              </S.DropdownItem>
            ))}
          </>
        </Dropdown>
      ) : (
        <>
          {showLoginPromo && (
            <LoginPromoModal
              isOpen={showLoginPromo}
              onRequestClose={toggleLoginPromo}
            />
          )}
          <ListsButton
            buttonStyle={buttonStyle}
            isActive={addedToList}
            onClick={toggleLoginPromo}
          />
        </>
      )}

      {alertId && isAuthenticated ? (
        <Dropdown
          showArrow={false}
          variant="secondary"
          grow="right"
          toggleElement={
            <S.ActionButton
              buttonStyle={buttonStyle}
              data-test="CreateAlertButton"
            >
              <S.Tooltip
                title={t('product', 'action-button.alert-tooltip')}
                horizontal={horizontal}
                width={180}
              >
                <S.Button
                  buttonStyle={buttonStyle}
                  variant={
                    buttonStyle === 'circle' ? 'iconOutlined' : 'outlined'
                  }
                  size={buttonStyle === 'circle' ? 'medium' : 'small'}
                  color={buttonStyle === 'circle' ? 'primary' : 'positive'}
                  active
                  icon={<BellIcon size={20} />}
                  iconOutlined={<BellOutlineIcon size={20} />}
                  component={<div />}
                >
                  <S.ButtonText>
                    {t('product', 'alert.create-button')}
                  </S.ButtonText>
                </S.Button>
              </S.Tooltip>
            </S.ActionButton>
          }
        >
          <S.DropdownItem onClick={removeAlert}>
            {t('product', 'alert.delete-button')}
          </S.DropdownItem>
          <S.DropdownItem onClick={editAlert}>
            {t('product', 'alert.edit-button')}
          </S.DropdownItem>
        </Dropdown>
      ) : (
        <S.ActionButton buttonStyle={buttonStyle} data-test="CreateAlertButton">
          <S.Tooltip
            title={t('product', 'action-button.alert-tooltip')}
            width={180}
          >
            <S.Button
              buttonStyle={buttonStyle}
              color="primary"
              variant={buttonStyle === 'circle' ? 'iconOutlined' : 'outlined'}
              size={buttonStyle === 'circle' ? 'medium' : 'small'}
              icon={<BellIcon size={20} />}
              iconOutlined={<BellOutlineIcon size={20} />}
              component={<div />}
              ariaLabel={t('product', 'alert.create-button')}
              onClick={() => {
                onAction('alert-create');
              }}
            >
              <S.ButtonText>{t('product', 'alert.create-button')}</S.ButtonText>
            </S.Button>
          </S.Tooltip>
        </S.ActionButton>
      )}

      <S.ActionButton buttonStyle={buttonStyle} data-test="CompareButton">
        <S.Tooltip
          title={t('product', 'action-button.compare-tooltip')}
          width={180}
          align="right"
        >
          <S.Button
            buttonStyle={buttonStyle}
            variant={buttonStyle === 'circle' ? 'iconOutlined' : 'outlined'}
            size={buttonStyle === 'circle' ? 'medium' : 'small'}
            color="primary"
            icon={<CompareIcon size={20} />}
            iconOutlined={<CompareIcon size={20} />}
            ariaLabel={t('product', 'product-compare.compare')}
            component={
              <Link
                to={linkTo('productCompare', {
                  category: product.category,
                  products: [product],
                })}
                rel="nofollow"
              />
            }
          >
            <S.ButtonText>
              {t('product', 'product-compare.compare')}
            </S.ButtonText>
          </S.Button>
        </S.Tooltip>
      </S.ActionButton>
    </S.ProductActionButtons>
  );
};

type ListsButtonProps = {
  component?: React.ReactElement;
  buttonStyle: ButtonStyle;
  isActive: boolean;
  onClick?: (e: React.MouseEvent) => void;
};
const ListsButton = ({
  component,
  buttonStyle,
  isActive,
  onClick,
}: ListsButtonProps) => {
  const { t } = useAppContext();

  return (
    <S.ActionButton buttonStyle={buttonStyle} data-test="SaveToListButton">
      <S.Tooltip
        title={t('product', 'action-button.list-tooltip')}
        align="left"
        width={180}
      >
        <S.Button
          buttonStyle={buttonStyle}
          variant={buttonStyle === 'circle' ? 'iconOutlined' : 'outlined'}
          size={buttonStyle === 'circle' ? 'medium' : 'small'}
          color={isActive && buttonStyle === 'square' ? 'positive' : 'primary'}
          icon={<HeartIcon />}
          iconOutlined={<HeartOutlineIcon />}
          active={isActive}
          component={component}
          ariaLabel={t('common', 'button.save')}
          onClick={onClick}
        >
          <S.ButtonText>{t('common', 'button.save')}</S.ButtonText>
        </S.Button>
      </S.Tooltip>
    </S.ActionButton>
  );
};

export default ProductActionButtons;
